import { useCallback, useEffect, useState } from 'react'

import { useSelector } from 'react-redux'
import { PlaceInterface } from '../../../common/types/interfaces'
import { getSalesApi } from '../../../salesApi/salesApi'
import { selectCurrentCluster } from '../../../store/slices/currentClusterSlice'

const initialValue: PlaceInterface = {
  name: '',
  description: '',
  placeNumber: '',
  storeId: '',
  isActive: true,
  sortOrder: 0,
  slotDurationMinutes: '',
  slotIntervalMinutes: '',
  breakBetweenSlotsMinutes: '',
  minBookingDurationMinutes: '',
  maxBookingDurationMinutes: '',
  cancellationDeadlineMinutes: '',
  checkinTime: '',
  checkoutTime: '',
  itemIds: [],
  categoryIds: [],
}

export const usePlace = () => {
  const [modalActive, setModalActive] = useState<boolean>(false)
  const [detailsModalActive, setDetailsModalActive] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [salesApi] = useState(getSalesApi())
  const [data, setData] = useState<PlaceInterface>(initialValue)
  const [list, setList] = useState<PlaceInterface[]>([])
  const [current, setCurrent] = useState<PlaceInterface>()
  const currentCluster = useSelector(selectCurrentCluster)
  const [loading, setLoading] = useState(true)

  const getList = useCallback(async () => {
    try {
      const result = await salesApi.getPlaceList()
      if (result) setList(result.data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }, [salesApi])

  const startCreate = () => {
    setCurrent(undefined)
    setData(initialValue)
    setEdit(false)
    setModalActive(true)
  }

  const startEdit = (el: PlaceInterface) => {
    setCurrent({ ...el })
    setData({
      ...el,
      itemIds: el.placeItems?.map((item: { itemId: number }) => item.itemId) || [],
      categoryIds: el.placeCategories?.map((cat: { categoryId: number }) => cat.categoryId) || [],
    })
    setEdit(true)
    setModalActive(true)
  }

  const startDuplicate = useCallback((sourceItem: PlaceInterface) => {
    const { id, createdAt, updatedAt, uuid, clusterId, ...rest } = sourceItem

    const duplicatedItem: PlaceInterface = {
      ...rest,
      name: `${sourceItem.name} (копия)`,
      itemIds: sourceItem.placeItems?.map((item: { itemId: number }) => item.itemId) || [],
      categoryIds:
        sourceItem.placeCategories?.map((cat: { categoryId: number }) => cat.categoryId) || [],
    }

    setCurrent(duplicatedItem)
    setEdit(false)
    setData(duplicatedItem)
    setModalActive(true)
  }, [])

  const showDetails = useCallback((el: PlaceInterface) => {
    setCurrent(el)
    setDetailsModalActive(true)
  }, [])

  useEffect(() => {
    if (currentCluster) getList()
  }, [currentCluster, getList])

  useEffect(() => {
    if (!modalActive) {
      setData(initialValue)
      setEdit(false)
    }
  }, [modalActive])

  return {
    getList,
    modalActive,
    setModalActive,
    startCreate,
    startEdit,
    startDuplicate,
    edit,
    data,
    setData,
    list,
    detailsModalActive,
    setDetailsModalActive,
    showDetails,
    current,
    loading,
  }
}
